import React, { useState, useRef, useEffect } from "react";
import "../css/LoginPage.css";
import { generateOtp, loginWithOtp } from "../service/api/auth";
import { useNavigate } from "react-router-dom";

const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.55 19a19.45 19.45 0 0 1-6-6A19.79 19.79 0 0 1 3.09 4.18 2 2 0 0 1 5.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
);

const SpinnerIcon = () => (
    <svg className="lp-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
);

const OTP_LENGTH = 6;

export default function OtpLoginPage() {
    const [step,      setStep]    = useState("phone");   // "phone" | "otp"
    const [phone,     setPhone]   = useState("");
    const [otp,       setOtp]     = useState(Array(OTP_LENGTH).fill(""));
    const [loading,   setLoading] = useState(false);
    const [error,     setError]   = useState("");
    const [fieldErr,  setFieldErr] = useState({});
    const [countdown, setCountdown] = useState(0);

    const otpRefs = useRef([]);
    const navigate = useNavigate();

    // Countdown timer for resend
    useEffect(() => {
        if (countdown <= 0) return;
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [countdown]);

    // ── Step 1: Send OTP ────────────────────────────────────────────────────
    const validatePhone = () => {
        const errs = {};
        const digits = phone.replace(/\D/g, "");
        if (!phone.trim())       errs.phone = "Phone number is required.";
        else if (digits.length < 10) errs.phone = "Enter a valid phone number.";
        setFieldErr(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setError("");
        if (!validatePhone()) return;

        setLoading(true);
        try {
            const res = await generateOtp(phone.trim());
            if (!res.otp) throw new Error(res.message || "Could not send OTP.");
            setStep("otp");
            setCountdown(30);
            setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Verify OTP ──────────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return; // digits only
        const next = [...otp];
        next[index] = value.slice(-1);    // max 1 char per box
        setOtp(next);
        setError("");

        if (value && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        const next = Array(OTP_LENGTH).fill("");
        pasted.split("").forEach((ch, i) => { next[i] = ch; });
        setOtp(next);
        otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError("");
        const otpValue = otp.join("");
        if (otpValue.length < OTP_LENGTH) {
            setError("Please enter the complete OTP.");
            return;
        }

        setLoading(true);
        try {
            const data = await loginWithOtp(phone.trim(), otpValue);
            // Redirect based on dashboard type returned from backend
            if (data.dashboard === "admin") {
                navigate("/", { replace: true });
            } else {
                navigate("/manager", { replace: true });
            }
        } catch (err) {
            setError(err.message);
            setOtp(Array(OTP_LENGTH).fill(""));
            setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0) return;
        setError("");
        setOtp(Array(OTP_LENGTH).fill(""));
        setLoading(true);
        try {
            const res = await generateOtp(phone.trim());
            if (!res.otp) throw new Error(res.message || "Could not resend OTP.");
            setCountdown(30);
            setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="lp-root">
            <div className="lp-bg-blob lp-bg-blob--1" />
            <div className="lp-bg-blob lp-bg-blob--2" />

            <div className="lp-card">
                <div className="lp-logo">
                    <img
                        src="https://food.totalfresh.co/assets/img/logos/logo.png?v=1613690452DQ1WV"
                        alt="Logo"
                        className="lp-logo-leaf"
                    />
                </div>

                <div className="lp-heading">
                    <h1>{step === "phone" ? "Welcome back" : "Enter OTP"}</h1>
                    <p>
                        {step === "phone"
                            ? "Sign in with your phone number"
                            : <>OTP sent to <strong>{phone}</strong></>
                        }
                    </p>
                </div>

                {error && (
                    <div className="lp-error-banner">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-4.75a.75.75 0 001.5 0v-4.5a.75.75 0 00-1.5 0v4.5zm.75-7.5a1 1 0 110 2 1 1 0 010-2z"/>
                        </svg>
                        {error}
                    </div>
                )}

                {/* ── Step 1: Phone ── */}
                {step === "phone" && (
                    <form className="lp-form" onSubmit={handleSendOtp} noValidate>
                        <div className={`lp-field${fieldErr.phone ? " lp-field--error" : ""}`}>
                            <label className="lp-label" htmlFor="lp-phone">Phone Number</label>
                            <div className="lp-input-wrap">
                                <span className="lp-input-icon"><PhoneIcon /></span>
                                <input
                                    id="lp-phone"
                                    type="tel"
                                    className="lp-input"
                                    placeholder="+91 98765 43210"
                                    value={phone}
                                    autoComplete="tel"
                                    onChange={(e) => {
                                        setPhone(e.target.value);
                                        setFieldErr({});
                                    }}
                                />
                            </div>
                            {fieldErr.phone && <span className="lp-field-err">{fieldErr.phone}</span>}
                        </div>

                        <button className="lp-submit" type="submit" disabled={loading}>
                            {loading ? <><SpinnerIcon /> Sending OTP…</> : "Send OTP"}
                        </button>
                    </form>
                )}

                {/* ── Step 2: OTP boxes ── */}
                {step === "otp" && (
                    <form className="lp-form" onSubmit={handleVerifyOtp} noValidate>
                        <div className="lp-otp-group">
                            {otp.map((digit, i) => (
                                <input
                                    key={i}
                                    ref={(el) => (otpRefs.current[i] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    className={`lp-otp-box${error ? " lp-otp-box--error" : ""}`}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(i, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                    onPaste={i === 0 ? handleOtpPaste : undefined}
                                />
                            ))}
                        </div>

                        <button className="lp-submit" type="submit" disabled={loading}>
                            {loading ? <><SpinnerIcon /> Verifying…</> : "Verify & Sign In"}
                        </button>

                        <div className="lp-otp-footer">
                            <button
                                type="button"
                                className="lp-ghost-btn"
                                onClick={() => { setStep("phone"); setError(""); setOtp(Array(OTP_LENGTH).fill("")); }}
                            >
                                ← Change number
                            </button>
                            <button
                                type="button"
                                className={`lp-ghost-btn${countdown > 0 ? " lp-ghost-btn--muted" : ""}`}
                                onClick={handleResend}
                                disabled={countdown > 0 || loading}
                            >
                                {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}